import type { HttpPort, HttpRequest, HttpResponse } from '../chat/core/ports';

/**
 * The one owner of the access token's validity. Every request asks `freshAccess()` before it goes
 * out, and a 401 refreshes once and retries once. A timer cannot own this: WebView and browser
 * timers run on a clock that stops while the device sleeps or the OS freezes the app, so a
 * 5-minute token expires under a timer that thinks it has minutes left. The timer here only
 * refreshes ahead of use, so an awake app never pays for a refresh on a request.
 *
 * The cookie carries the token; this module tracks only when it expires, in local time.
 */

/** The login and refresh payload: the user profile plus the access token's expiry. */
export interface AccessProfile {
    /** Expiry in server epoch seconds. */
    access_exp: number;
    [field: string]: unknown;
}

/** A failed refresh, shaped like an axios error so the auth reducer's error handling reads it. */
export class RefreshError extends Error {
    constructor(
        message: string,
        readonly response?: { status: number; data: unknown },
    ) {
        super(message);
        this.name = 'RefreshError';
    }
}

export type RefreshOutcome = { ok: true; profile: AccessProfile } | { ok: false; error: RefreshError };

const TOKEN_ENDPOINTS = /\/api\/login\/token2\//;
// Refresh this long before expiry, which also absorbs the server Date header's 1 s resolution.
const MARGIN_MS = 60_000;
const NETWORK_RETRY_MS = 5_000;

type SessionState = 'starting' | 'active' | 'ended';

let state: SessionState = 'starting';
let validUntil = 0;
let pending: Promise<RefreshOutcome> | null = null;
let prewarm: ReturnType<typeof setTimeout> | undefined;
let refreshUrl = '';
let transport: HttpPort | null = null;
let listener: (outcome: RefreshOutcome) => void = () => undefined;
// Requests made before the first refresh settles wait for it, so none goes out on an expired token.
let settleFirst: () => void = () => undefined;
const firstSettled = new Promise<void>((resolve) => {
    settleFirst = resolve;
});

const schedule = (delayMs: number): void => {
    clearTimeout(prewarm);
    prewarm = setTimeout(() => void refreshAccess(), Math.max(delayMs, 0));
};

/** Record a token the server just issued. `serverDate` is the response's Date header. */
export const recordAccess = (profile: AccessProfile, serverDate: string | null): void => {
    const server = serverDate ? Date.parse(serverDate) : NaN;
    // The expiry is server time; shift it onto the local clock so a wrong phone clock is harmless.
    const offset = Number.isNaN(server) ? 0 : Date.now() - server;
    validUntil = profile.access_exp * 1000 + offset;
    state = 'active';
    settleFirst();
    schedule(validUntil - MARGIN_MS - Date.now());
};

/** Logout: forget the token and stop refreshing. */
export const endAccess = (): void => {
    state = 'ended';
    validUntil = 0;
    clearTimeout(prewarm);
    settleFirst();
};

/** Whether the first refresh of this session has answered. */
export const isAccessSettled = (): boolean => state !== 'starting';

/** The redux side of the session: sees every refresh outcome. */
export const onRefreshOutcome = (next: (outcome: RefreshOutcome) => void): void => {
    listener = next;
};

const requestRefresh = async (): Promise<RefreshOutcome> => {
    let response: HttpResponse;
    try {
        response = await (transport as HttpPort).fetch({ method: 'POST', url: refreshUrl });
    } catch (error) {
        schedule(NETWORK_RETRY_MS);
        return { ok: false, error: new RefreshError(error instanceof Error ? error.message : 'Network Error') };
    }
    const text = await response.text();
    if (response.ok) {
        const profile = JSON.parse(text) as AccessProfile;
        recordAccess(profile, response.headers.get('date'));
        return { ok: true, profile };
    }
    let data: unknown = text;
    try {
        data = JSON.parse(text);
    } catch {
        // A proxy error page: keep the text.
    }
    if (response.status >= 500) schedule(NETWORK_RETRY_MS);
    else endAccess();
    return {
        ok: false,
        error: new RefreshError(`token refresh failed: HTTP ${response.status}`, { status: response.status, data }),
    };
};

/** Refresh now. Concurrent callers share one request. */
export const refreshAccess = (): Promise<RefreshOutcome> => {
    pending ??= requestRefresh()
        .then((outcome) => {
            listener(outcome);
            return outcome;
        })
        .finally(() => {
            pending = null;
            if (state === 'starting') state = 'ended';
            settleFirst();
        });
    return pending;
};

/** Resolve once the token is good for the next request, refreshing first if it is not. */
export const freshAccess = async (): Promise<void> => {
    if (state === 'starting') await firstSettled;
    if (pending) await pending;
    else if (state === 'active' && Date.now() >= validUntil - MARGIN_MS) await refreshAccess();
};

/** Start the session at app boot: the first refresh tells whether the saved cookie still logs in. */
export const startAccess = (port: HttpPort, url: string): Promise<RefreshOutcome> => {
    transport = port;
    refreshUrl = url;
    return refreshAccess();
};

/**
 * Put the session in front of a transport: each request waits for a valid token, and a 401
 * refreshes once and retries once. The token endpoints pass straight through.
 */
export const withAccessToken = (inner: HttpPort): HttpPort => ({
    async fetch(request: HttpRequest): Promise<HttpResponse> {
        if (TOKEN_ENDPOINTS.test(request.url)) return inner.fetch(request);
        await freshAccess();
        const response = await inner.fetch(request);
        if (response.status !== 401 || state !== 'active') return response;
        const outcome = await refreshAccess();
        return outcome.ok ? inner.fetch(request) : response;
    },
});
